import React, { JSX, useRef, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import CyclingLoader from '../../shared/CyclingLoader';
import { EventRow, EventWithDetails } from '../types';
import { BTN_PRIMARY, BTN_ICON } from '../utils';
import { EventOverviewEventCard } from './EventOverviewEventCard';
import { EventsFiltersMenu } from './EventsFiltersMenu';
import type { EventFiltersValue, RaceStatusAdvanceOptions } from '../types/EventsOverviewTypes';

type Props = {
  loading: boolean;
  filteredEvents: EventRow[];
  totalEvents: number;
  details: Record<string, EventWithDetails>;
  expanded: Record<string, boolean>;
  globalUserIdToDisplayName: Record<string, string>;
  isAdmin: boolean;
  isOperator: boolean;
  /** When true, show Create new event (admins and Cognito operators who may create their own event). */
  canCreateEvent: boolean;
  selectedEventId?: string | null;
  onSelectEvent?: (eventId: string) => void;
  /** `eventId#raceId` while a race status update is in flight. */
  progressingRaceKey?: string | null;
  onAdvanceRace?: (
    eventId: string,
    race: import('../types/EventsOverviewTypes').EventRaceSummaryRow,
    nextStatus: string,
    options?: RaceStatusAdvanceOptions,
  ) => void;
  onSoftDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  onHardDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  onCreateEvent: () => void;
  /** Current event filter values (rendered in the Filters popover). */
  filters: EventFiltersValue;
  onChangeFilters: (next: EventFiltersValue) => void;
  /** Count of active filters (excludes defaults), shown as a badge. */
  activeFilterCount?: number;
  onToggleExpanded: (eventId: string) => void;
  onOpenAssignOperatorDialog: (eventId: string) => void;
  onOpenDevicePackaging?: (eventId: string, raceId: string) => void;
  onOpenRecallDialog?: (eventId: string, raceId: string) => void;
  onSoftDeleteEvent?: (eventId: string) => void;
  onHardDeleteEvent?: (eventId: string) => void;
  onRemoveOperator?: (eventId: string, userId: string) => void;
  onSetDeviceRecalled?: (
    eventId: string,
    deviceId: string,
    recalled: 0 | 1,
    options?: { raceId?: string; retrievedPhase?: string },
  ) => void;
  onRefreshEventDetails?: (eventId: string, raceId?: string) => void | Promise<void>;
  refreshingDetailsByEventId?: Record<string, boolean>;
  eventDetailsRefreshVersionByEventId?: Record<string, number>;
  eventDetailsLoadedByEventId?: Record<string, boolean>;
  /** Open Devices tab scoped to this event (optional race). */
  onNavigateToDevices?: (payload: {
    eventId: string;
    eventName?: string | null;
    raceId?: string | null;
    unretrievedOnly?: boolean;
  }) => void;
  loadingPhrases?: string[];
};

const DEFAULT_LOADING_PHRASES = [
  'Loading events…',
  'Refreshing event details…',
  'Fetching race data…',
];

export function EventsOverviewMainPanel(props: Props): JSX.Element {
  const {
    loading,
    filteredEvents,
    totalEvents,
    details,
    expanded,
    globalUserIdToDisplayName,
    isAdmin,
    isOperator,
    canCreateEvent,
    selectedEventId,
    onSelectEvent,
    progressingRaceKey,
    onAdvanceRace,
    onSoftDeleteRace,
    onHardDeleteRace,
    onCreateEvent,
    filters,
    onChangeFilters,
    activeFilterCount = 0,
    onToggleExpanded,
    onOpenAssignOperatorDialog,
    onOpenDevicePackaging,
    onOpenRecallDialog,
    onSoftDeleteEvent,
    onHardDeleteEvent,
    onRemoveOperator,
    onSetDeviceRecalled,
    onRefreshEventDetails,
    refreshingDetailsByEventId,
    eventDetailsRefreshVersionByEventId,
    eventDetailsLoadedByEventId,
    onNavigateToDevices,
    loadingPhrases = DEFAULT_LOADING_PHRASES,
  } = props;

  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-xs text-gray-600 dark:text-slate-400">
          Showing <span className="font-semibold text-gray-800 dark:text-slate-200">{filteredEvents.length}</span>{' '}
          of <span className="font-semibold text-gray-800 dark:text-slate-200">{totalEvents}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              ref={filtersButtonRef}
              type="button"
              onClick={() => setFiltersMenuOpen((v) => !v)}
              aria-expanded={filtersMenuOpen}
              aria-haspopup="dialog"
              aria-label="Filters"
              title="Filters"
              className={
                activeFilterCount > 0
                  ? 'relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-300 bg-blue-50 text-blue-700 shadow-sm transition-all duration-100 hover:bg-blue-100 dark:h-8 dark:w-8 dark:border-blue-700/60 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/50'
                  : `relative ${BTN_ICON}`
              }
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-none text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <EventsFiltersMenu
              open={filtersMenuOpen}
              onClose={() => setFiltersMenuOpen(false)}
              anchorRef={filtersButtonRef}
              filters={filters}
              onChangeFilters={onChangeFilters}
            />
          </div>
          {canCreateEvent ? (
            <button type="button" onClick={onCreateEvent} className={BTN_PRIMARY}>
              Create new event
            </button>
          ) : null}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="flex-1 min-h-0 flex items-center justify-center relative min-h-[240px]">
          {!loading ? <div className="text-sm text-gray-500 dark:text-slate-400">No events found.</div> : null}
          {loading ? (
            <div className="flex items-center justify-center">
              <CyclingLoader phrases={loadingPhrases} size="md" />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto relative">
          <ul className="space-y-4 list-none m-0 p-0">
            {filteredEvents.map((ev) => (
              <li key={ev.eventId}>
                <EventOverviewEventCard
                  ev={ev}
                  details={details[ev.eventId]}
                  isExpanded={expanded[ev.eventId] ?? false}
                  isSelected={selectedEventId === ev.eventId}
                  globalUserIdToDisplayName={globalUserIdToDisplayName}
                  isAdmin={isAdmin}
                  isOperator={isOperator}
                  progressingRaceKey={progressingRaceKey}
                  onAdvanceRace={onAdvanceRace}
                  onSoftDeleteRace={onSoftDeleteRace}
                  onHardDeleteRace={onHardDeleteRace}
                  onToggleExpanded={onToggleExpanded}
                  onOpenAssignOperatorDialog={onOpenAssignOperatorDialog}
                  onOpenDevicePackaging={onOpenDevicePackaging}
                  onOpenRecallDialog={onOpenRecallDialog}
                  onSoftDeleteEvent={onSoftDeleteEvent}
                  onHardDeleteEvent={onHardDeleteEvent}
                  onRemoveOperator={onRemoveOperator}
                  onSetDeviceRecalled={onSetDeviceRecalled}
                  onRefreshEventDetails={onRefreshEventDetails}
                  isRefreshingEventDetails={!!refreshingDetailsByEventId?.[ev.eventId]}
                  isDetailsLoaded={!!eventDetailsLoadedByEventId?.[ev.eventId]}
                  eventDetailsRefreshVersion={eventDetailsRefreshVersionByEventId?.[ev.eventId] ?? 0}
                  onSelectEvent={onSelectEvent}
                  onNavigateToDevices={onNavigateToDevices}
                />
              </li>
            ))}
          </ul>
          {loading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50/80 dark:bg-slate-800/80 backdrop-blur pointer-events-none">
              <CyclingLoader phrases={loadingPhrases} size="md" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
